import ContentLayout from '@/components/layout/ContentLayout';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ROUTE } from '@/routes/constants';
import { archiveDuplicateRewards, archiveStudentRecords, getDuplicateRewardRecords, getStudentRewardHistory, searchStudents } from '@/services/generatedAPIs';
import { DuplicateRewardRecordsResponse, OverCapPracticeGroup, PointsSystem, StudentRewardHistoryResponse, StudentSummary } from '@/services/generatedAPIs.schemas';
import { AlertTriangle, Archive, ChevronLeft, ChevronRight, Eye, Search, Users } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

const PAGE_SIZE = 20;
const SEARCH_PAGE_SIZE = 10;

const formatMetadata = (raw?: string) => {
  if (!raw) return '-';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const PointsRecordsTable = ({ records, onSelect }: { records: PointsSystem[]; onSelect: (record: PointsSystem) => void }) => (
  <div className="overflow-x-auto">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-xs">Point ID</TableHead>
          <TableHead className="text-xs">Student ID</TableHead>
          <TableHead className="text-xs">Awarded At</TableHead>
          <TableHead className="text-xs">Event Type</TableHead>
          <TableHead className="text-xs">Event Source</TableHead>
          <TableHead className="text-xs">Source</TableHead>
          <TableHead className="text-xs">Currency</TableHead>
          <TableHead className="text-xs">Amount</TableHead>
          <TableHead className="text-xs">Multiplier</TableHead>
          <TableHead className="text-xs">Difficulty</TableHead>
          <TableHead className="text-xs">Status</TableHead>
          <TableHead className="text-xs">Description</TableHead>
          <TableHead className="text-xs"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {records.map((record) => (
          <Fragment key={record.pointId}>
            <TableRow>
              <TableCell className="text-xs">{record.pointId ?? '-'}</TableCell>
              <TableCell className="text-xs">{record.studentId ?? '-'}</TableCell>
              <TableCell className="text-xs whitespace-nowrap">{record.awardedAt ?? '-'}</TableCell>
              <TableCell className="text-xs">{record.eventType ?? '-'}</TableCell>
              <TableCell className="text-xs whitespace-nowrap">
                {(record.eventSourceType ?? '-') + ' #' + (record.eventSourceId ?? '-')}
              </TableCell>
              <TableCell className="text-xs">{record.pointsSource ?? '-'}</TableCell>
              <TableCell className="text-xs">{record.currencyType ?? '-'}</TableCell>
              <TableCell className="text-xs">{record.pointsAmount ?? 0}</TableCell>
              <TableCell className="text-xs">{record.multiplier != null ? `x${record.multiplier}` : '-'}</TableCell>
              <TableCell className="text-xs">{record.difficultyLevel ?? '-'}</TableCell>
              <TableCell className="text-xs">{record.status ?? '-'}</TableCell>
              <TableCell className="text-xs max-w-xs truncate" title={record.description ?? ''}>{record.description ?? '-'}</TableCell>
              <TableCell>
                <Button variant="ghost" size="sm" onClick={() => onSelect(record)}>
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={13} className="p-0 border-b">
                <details className="px-4 py-1">
                  <summary className="cursor-pointer text-xs text-muted-foreground">Metadata</summary>
                  <pre className="bg-background rounded-md p-2 mt-1 text-xs whitespace-pre-wrap break-words max-h-40 overflow-auto">
                    {formatMetadata(record.metadata)}
                  </pre>
                </details>
              </TableCell>
            </TableRow>
          </Fragment>
        ))}
      </TableBody>
    </Table>
  </div>
);

const OverCapGroupCard = ({ group, onSelectRecord }: { group: OverCapPracticeGroup; onSelectRecord: (record: PointsSystem) => void }) => (
  <div className="bg-muted rounded-xl p-3 space-y-2">
    <div className="flex justify-between items-baseline">
      <p className="font-medium">Student #{group.studentId}</p>
      <p className="text-xs text-muted-foreground">{group.recordsToArchive ?? group.records?.length ?? 0} to archive</p>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
      <div>
        <p className="text-xs text-muted-foreground">Question Info ID</p>
        <p>{group.questionInfoId ?? '-'}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Month</p>
        <p>{group.yearMonth ?? '-'}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Blocked Assignments</p>
        <p>{group.blockedAssignmentIds?.length ? group.blockedAssignmentIds.join(', ') : '-'}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Records to Archive</p>
        <p>{group.recordsToArchive ?? 0}</p>
      </div>
    </div>
    {group.records && group.records.length > 0 && (
      <div className="mt-2">
        <p className="text-xs font-medium mb-1">Records:</p>
        <PointsRecordsTable records={group.records} onSelect={onSelectRecord} />
      </div>
    )}
  </div>
);

const GamificationRecordsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [data, setData] = useState<StudentRewardHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [archivingStudent, setArchivingStudent] = useState(false);
  const [duplicateResult, setDuplicateResult] = useState<DuplicateRewardRecordsResponse | null>(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [archivingDuplicates, setArchivingDuplicates] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<PointsSystem | null>(null);

  // Student search by name/school
  const [searchName, setSearchName] = useState('');
  const [searchSchool, setSearchSchool] = useState('');
  const [searchResults, setSearchResults] = useState<StudentSummary[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchHistory = async (targetPage: number, targetEmail?: string) => {
    const emailToUse = targetEmail ?? email.trim();
    if (!emailToUse) return;
    setLoading(true);
    try {
      const response = await getStudentRewardHistory({ email: emailToUse, page: targetPage, size: PAGE_SIZE });
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200 && response.data) {
        setData(response.data);
        setPage(targetPage);
      } else {
        toast.error(response.message || 'Failed to fetch reward history');
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error fetching reward history', error);
      toast.error('Failed to fetch reward history');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setData(null);
    fetchHistory(1);
  };

  const handleArchiveStudent = async () => {
    if (!email.trim()) return;
    setArchivingStudent(true);
    try {
      const response = await archiveStudentRecords({ emails: [email.trim()] });
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200) {
        toast.success('Student records archived successfully');
        fetchHistory(page);
      } else {
        toast.error(response.message || 'Failed to archive student records');
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error archiving student records', error);
      toast.error('Failed to archive student records');
    } finally {
      setArchivingStudent(false);
    }
  };

  const refreshDuplicates = async () => {
    setCheckingDuplicates(true);
    try {
      const response = await getDuplicateRewardRecords();
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200 && response.data) {
        setDuplicateResult(response.data);
      } else {
        toast.error(response.message || 'Failed to check duplicate records');
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error checking duplicate records', error);
      toast.error('Failed to check duplicate records');
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleCheckDuplicates = () => refreshDuplicates();

  const handleArchiveDuplicates = async () => {
    setArchivingDuplicates(true);
    try {
      const response = await archiveDuplicateRewards();
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200) {
        const {
          recordsArchived = 0, groupsProcessed = 0, studentsAffected = 0,
          overCapRecordsArchived = 0, overCapGroupsProcessed = 0, overCapStudentsAffected = 0,
        } = response.data ?? {};
        toast.success(
          `Archived ${recordsArchived} duplicate record(s) across ${groupsProcessed} group(s) ` +
          `and ${overCapRecordsArchived} over-cap record(s) across ${overCapGroupsProcessed} group(s) ` +
          `(${studentsAffected + overCapStudentsAffected} student(s) affected)`,
        );
        await refreshDuplicates();
      } else {
        toast.error(response.message || 'Failed to archive duplicate records');
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error archiving duplicate records', error);
      toast.error('Failed to archive duplicate records');
    } finally {
      setArchivingDuplicates(false);
    }
  };

  const fetchStudentSearch = async (targetPage: number) => {
    if (!searchName.trim() && !searchSchool.trim()) return;
    setSearching(true);
    try {
      const response = await searchStudents({
        name: searchName.trim() || undefined,
        schoolName: searchSchool.trim() || undefined,
        page: targetPage,
        size: SEARCH_PAGE_SIZE,
      });
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200 && response.data) {
        setSearchResults(response.data.students ?? []);
        setSearchTotal(response.data.total ?? 0);
        setSearchPage(targetPage);
        setHasSearched(true);
      } else {
        toast.error(response.message || 'Failed to search students');
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error searching students', error);
      toast.error('Failed to search students');
    } finally {
      setSearching(false);
    }
  };

  const handleStudentSearch = () => {
    setSearchResults([]);
    setHasSearched(false);
    fetchStudentSearch(1);
  };

  const handleSelectStudent = (student: StudentSummary) => {
    if (student.email) {
      setEmail(student.email);
      setData(null);
      fetchHistory(1, student.email);
    }
  };

  const searchTotalPages = Math.max(1, Math.ceil(searchTotal / SEARCH_PAGE_SIZE));

  const totalPages = data ? Math.max(1, Math.ceil((data.totalTransactions ?? 0) / PAGE_SIZE)) : 1;

  return (
    <ContentLayout title="Student Reward History">
      {/* Student Search by Name/School */}
      <div className="m-4 bg-muted rounded-2xl p-4 space-y-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Users className="h-5 w-5" />
          Search Students
        </h3>
        <div className="flex items-start gap-3">
          <div className="grid gap-1.5 flex-1 max-w-xs">
            <Label htmlFor="searchName">Student Name</Label>
            <Input
              id="searchName"
              placeholder="Name (Chinese or English)"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleStudentSearch()}
            />
          </div>
          <div className="grid gap-1.5 flex-1 max-w-xs">
            <Label htmlFor="searchSchool">School Name</Label>
            <Input
              id="searchSchool"
              placeholder="School name"
              value={searchSchool}
              onChange={(e) => setSearchSchool(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleStudentSearch()}
            />
          </div>
          <Button className="m-auto ml-0" onClick={handleStudentSearch} disabled={searching || (!searchName.trim() && !searchSchool.trim())}>
            <Search className="h-4 w-4 mr-2" />
            {searching ? 'Searching...' : 'Search'}
          </Button>
        </div>
        {hasSearched && (
          searchResults.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>English Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Grade</TableHead>
                    <TableHead>KP (All-time)</TableHead>
                    <TableHead>GS (All-time)</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {searchResults.map((student) => (
                    <TableRow key={student.studentId} className="cursor-pointer hover:bg-accent" onClick={() => handleSelectStudent(student)}>
                      <TableCell>{student.name ?? '-'}</TableCell>
                      <TableCell>{student.nameEn ?? '-'}</TableCell>
                      <TableCell>{student.email ?? '-'}</TableCell>
                      <TableCell>{student.schoolName ?? '-'}</TableCell>
                      <TableCell>{student.grade ?? '-'}</TableCell>
                      <TableCell>{student.kpAllTime ?? 0}</TableCell>
                      <TableCell>{student.gsAllTime ?? 0}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleSelectStudent(student); }}>
                          View Records
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {searchTotalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {searchPage} of {searchTotalPages} ({searchTotal} students)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={searchPage <= 1 || searching} onClick={() => fetchStudentSearch(searchPage - 1)}>
                      <ChevronLeft className="h-4 w-4" /> Previous
                    </Button>
                    <Button variant="outline" size="sm" disabled={searchPage >= searchTotalPages || searching} onClick={() => fetchStudentSearch(searchPage + 1)}>
                      Next <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No students found matching your search.</p>
          )
        )}
      </div>

      {/* Search by Email */}
      <div className="flex items-end gap-3 m-4">
        <div className="grid gap-1.5 flex-1 max-w-sm">
          <Label htmlFor="email">Student Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="student@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <Button onClick={handleSearch} disabled={loading || !email.trim()}>
          <Search className="h-4 w-4 mr-2" />
          {loading ? 'Searching...' : 'Search'}
        </Button>
        <Button variant="outline" disabled={checkingDuplicates} onClick={handleCheckDuplicates}>
          <AlertTriangle className="h-4 w-4 mr-2" />
          {checkingDuplicates ? 'Checking...' : 'Check Problematic Records'}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={archivingStudent || !email.trim()}>
              <Archive className="h-4 w-4 mr-2" />
              {archivingStudent ? 'Archiving...' : 'Archive Records'}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Archive Student Records</AlertDialogTitle>
            </AlertDialogHeader>
            <p className="text-sm text-muted-foreground">
              This will archive all point records for <strong>{email.trim()}</strong>. This action cannot be undone. Are you sure?
            </p>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleArchiveStudent}>Confirm</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-muted rounded-xl p-4">
              <p className="text-sm text-muted-foreground">Student</p>
              <p className="text-lg font-semibold">{data.studentName ?? '-'}</p>
              <p className="text-xs text-muted-foreground">{data.schoolName ?? '-'}</p>
              <p className="text-xs text-muted-foreground">ID: {data.studentId ?? '-'}</p>
            </div>
            <div className="bg-muted rounded-xl p-4">
              <p className="text-sm text-muted-foreground">Knowledge Points</p>
              <p className="text-lg font-semibold">{data.kpAllTime ?? 0} <span className="text-sm font-normal text-muted-foreground">all-time</span></p>
              <p className="text-sm">{data.kpCurrentMonth ?? 0} <span className="text-xs text-muted-foreground">this month</span></p>
            </div>
            <div className="bg-muted rounded-xl p-4">
              <p className="text-sm text-muted-foreground">Game Score</p>
              <p className="text-lg font-semibold">{data.gsAllTime ?? 0} <span className="text-sm font-normal text-muted-foreground">all-time</span></p>
              <p className="text-sm">{data.gsCurrentMonth ?? 0} <span className="text-xs text-muted-foreground">this month</span></p>
            </div>
            <div className="bg-muted rounded-xl p-4">
              <p className="text-sm text-muted-foreground">Transaction Status</p>
              <div className="grid grid-cols-2 gap-x-3 text-sm mt-1">
                <span>Active: <strong>{data.activeCount ?? 0}</strong></span>
                <span>Late: <strong>{data.lateCount ?? 0}</strong></span>
                <span>Archived: <strong>{data.archivedCount ?? 0}</strong></span>
                <span>Voided: <strong>{data.voidedCount ?? 0}</strong></span>
              </div>
            </div>
          </div>

          {/* Transactions Table */}
          <div className="bg-muted rounded-2xl p-4">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-lg font-semibold">Transactions ({data.totalTransactions ?? 0})</h3>
              <p className="text-xs text-muted-foreground">Page {data.page ?? page} · Size {data.size ?? PAGE_SIZE}</p>
            </div>
            {data.transactions && data.transactions.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Point ID</TableHead>
                      <TableHead>Student ID</TableHead>
                      <TableHead>Awarded At</TableHead>
                      <TableHead>Event Type</TableHead>
                      <TableHead>Event Source</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Multiplier</TableHead>
                      <TableHead>Difficulty</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.transactions.map((tx) => (
                      <TableRow key={tx.pointId}>
                        <TableCell>{tx.pointId ?? '-'}</TableCell>
                        <TableCell>{tx.studentId ?? '-'}</TableCell>
                        <TableCell className="whitespace-nowrap">{tx.awardedAt ?? '-'}</TableCell>
                        <TableCell>{tx.eventType ?? '-'}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {(tx.eventSourceType ?? '-') + ' #' + (tx.eventSourceId ?? '-')}
                        </TableCell>
                        <TableCell>{tx.pointsSource ?? '-'}</TableCell>
                        <TableCell>{tx.currencyType ?? '-'}</TableCell>
                        <TableCell>{tx.pointsAmount ?? 0}</TableCell>
                        <TableCell>{tx.multiplier != null ? `x${tx.multiplier}` : '-'}</TableCell>
                        <TableCell>{tx.difficultyLevel ?? '-'}</TableCell>
                        <TableCell>{tx.status ?? '-'}</TableCell>
                        <TableCell className="max-w-xs truncate">{tx.description ?? '-'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => setSelectedTransaction(tx)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1 || loading}
                      onClick={() => fetchHistory(page - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages || loading}
                      onClick={() => fetchHistory(page + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex justify-center p-8 text-muted-foreground">No transactions found</div>
            )}
          </div>
        </div>
      )}
      {/* Duplicate Records Dialog */}
      <Dialog open={!!duplicateResult} onOpenChange={(open) => !open && setDuplicateResult(null)}>
        <DialogContent className="sm:max-w-[95vw] h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Problematic Records</DialogTitle>
          </DialogHeader>
          {duplicateResult && (
            <div className="space-y-4 flex-1 min-h-0 flex flex-col">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-muted rounded-xl p-3">
                  <p className="text-sm text-muted-foreground">Duplicate Groups</p>
                  <p className="text-xl font-semibold">{duplicateResult.duplicateGroupCount ?? 0}</p>
                </div>
                <div className="bg-muted rounded-xl p-3">
                  <p className="text-sm text-muted-foreground">Total Duplicate Records</p>
                  <p className="text-xl font-semibold">{duplicateResult.totalDuplicateRecords ?? 0}</p>
                </div>
                <div className="bg-muted rounded-xl p-3">
                  <p className="text-sm text-muted-foreground">Over-Cap Practice Groups</p>
                  <p className="text-xl font-semibold">{duplicateResult.overCapPracticeGroupCount ?? 0}</p>
                </div>
                <div className="bg-muted rounded-xl p-3">
                  <p className="text-sm text-muted-foreground">Over-Cap Practice Records</p>
                  <p className="text-xl font-semibold">{duplicateResult.overCapPracticeRecordsTotal ?? 0}</p>
                </div>
              </div>
              {(duplicateResult.duplicateGroups?.length || duplicateResult.overCapPracticeGroups?.length) ? (
                <ScrollArea className="flex-1 min-h-0">
                  <div className="space-y-4">
                    {duplicateResult.duplicateGroups?.map((group, idx) => (
                      <div key={`dup-${idx}`} className="bg-muted rounded-xl p-3 space-y-2">
                        <div className="flex justify-between items-baseline">
                          <p className="font-medium">Student #{group.studentId}</p>
                          <p className="text-xs text-muted-foreground">{group.count} duplicates</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">Question Info ID</p>
                            <p>{group.questionInfoId ?? '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Source</p>
                            <p>{group.pointsSource ?? '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Amount</p>
                            <p>{group.pointsAmount ?? 0} ({group.currencyType ?? '-'})</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Event Type</p>
                            <p>{group.eventType ?? '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Event Source</p>
                            <p>{(group.eventSourceType ?? '-') + ' #' + (group.eventSourceId ?? '-')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Difficulty</p>
                            <p>{group.difficultyLevel ?? '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Status</p>
                            <p>{group.status ?? '-'}</p>
                          </div>
                        </div>
                        {group.description && (
                          <div>
                            <p className="text-xs text-muted-foreground">Description</p>
                            <p className="text-sm whitespace-pre-wrap break-words">{group.description}</p>
                          </div>
                        )}
                        {group.metadata && (
                          <div>
                            <p className="text-xs text-muted-foreground">Metadata</p>
                            <pre className="bg-background rounded-md p-2 text-xs whitespace-pre-wrap break-words max-h-40 overflow-auto">
                              {formatMetadata(group.metadata)}
                            </pre>
                          </div>
                        )}
                        {group.records && group.records.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium mb-1">Records:</p>
                            <PointsRecordsTable records={group.records} onSelect={setSelectedTransaction} />
                          </div>
                        )}
                      </div>
                    ))}
                    {duplicateResult.overCapPracticeGroups && duplicateResult.overCapPracticeGroups.length > 0 && (
                      <>
                        <h4 className="text-sm font-semibold pt-2">Over-Cap Practice Rewards</h4>
                        {duplicateResult.overCapPracticeGroups.map((group, idx) => (
                          <OverCapGroupCard
                            key={`overcap-${idx}`}
                            group={group}
                            onSelectRecord={setSelectedTransaction}
                          />
                        ))}
                      </>
                    )}
                  </div>
                </ScrollArea>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No problematic records found.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  disabled={
                    archivingDuplicates ||
                    checkingDuplicates ||
                    (!duplicateResult?.duplicateGroups?.length && !duplicateResult?.overCapPracticeGroups?.length)
                  }
                >
                  <Archive className="h-4 w-4 mr-2" />
                  {archivingDuplicates ? 'Archiving...' : 'Archive Problematic'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Archive Problematic Rewards</AlertDialogTitle>
                </AlertDialogHeader>
                <p className="text-sm text-muted-foreground">
                  This will archive {duplicateResult?.totalDuplicateRecords ?? 0} duplicate
                  record(s) across {duplicateResult?.duplicateGroupCount ?? 0} group(s)
                  (keeping the most recent in each group), and{' '}
                  {duplicateResult?.overCapPracticeRecordsTotal ?? 0} over-cap practice
                  reward record(s) across {duplicateResult?.overCapPracticeGroupCount ?? 0}{' '}
                  group(s). Affected students' scores and leaderboards will need to be
                  recalculated separately. This action cannot be undone. Are you sure?
                </p>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleArchiveDuplicates}>Confirm</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button variant="outline" onClick={() => setDuplicateResult(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction Details Dialog */}
      <Dialog open={!!selectedTransaction} onOpenChange={(open) => !open && setSelectedTransaction(null)}>
        <DialogContent className="max-w-2xl h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <ScrollArea className="flex-1 min-h-0">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Point ID</p>
                  <p>{selectedTransaction.pointId ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Student ID</p>
                  <p>{selectedTransaction.studentId ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Awarded At</p>
                  <p>{selectedTransaction.awardedAt ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p>{selectedTransaction.status ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Points Source</p>
                  <p>{selectedTransaction.pointsSource ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Event Type</p>
                  <p>{selectedTransaction.eventType ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Event Source Type</p>
                  <p>{selectedTransaction.eventSourceType ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Event Source ID</p>
                  <p>{selectedTransaction.eventSourceId ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Currency</p>
                  <p>{selectedTransaction.currencyType ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Amount</p>
                  <p>{selectedTransaction.pointsAmount ?? 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Multiplier</p>
                  <p>{selectedTransaction.multiplier != null ? `x${selectedTransaction.multiplier}` : '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Difficulty Level</p>
                  <p>{selectedTransaction.difficultyLevel ?? '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Description</p>
                  <p className="whitespace-pre-wrap break-words">{selectedTransaction.description ?? '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-muted-foreground">Metadata</p>
                  <pre className="bg-background rounded-md p-2 text-xs whitespace-pre-wrap break-words max-h-64 overflow-auto">
                    {formatMetadata(selectedTransaction.metadata)}
                  </pre>
                </div>
              </div>
            </ScrollArea>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedTransaction(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ContentLayout>
  );
};

export default GamificationRecordsPage;
